import { IS_TEACHER } from '@constants';
import {
  E_GAME_MODES,
  GAMES,
  HOTSEAT_CONFIGS,
  JUMBLE_CONFIGS,
  TABOO_CONFIGS
} from '@constants/gameConfigs';
import { IStudentDto } from '@dto';
import { useGameContext } from 'context/GameContext';
import AppAudio from 'core/audio';
import AppSignalR, { SIGNALR_EVENTS } from 'core/signalR';
import AppValidation from 'core/validation';
import { useResolverForm } from 'hooks';
import { usePopup } from 'hooks/usePopup';
import { isEmpty, set } from 'lodash';
import React, { memo, useCallback, useMemo } from 'react';
import { Controller, FormProvider, useWatch } from 'react-hook-form';
import trans from 'translation';
import {
  KButton,
  KColors,
  KContainer,
  KDims,
  KInput,
  KLabel,
  KListItem,
  useMount
} from 'uikit';
import { UIUtils } from 'utils';
import { v4 as uuidv4 } from 'uuid';

import GameCode from './GameCode';
import { IFormData } from './helpers';
import ObservationItem from './ObservationItem';
import TeamItem from './TeamItem';

import InGamePackageView from '../InGame.PackageView';

const WaitingRoom = () => {
  const { roomInfo, isHotseat, isJumble } = useGameContext();

  const { gameSetup } = roomInfo || {};

  const gameType = gameSetup?.type ?? GAMES.TABOO;
  const gameMode = gameSetup?.gameMode ?? '';

  const isWholeClass = gameMode === E_GAME_MODES.WHOLE_CLASS.id;

  const gameConfigs = useMemo(() => {
    switch (gameType) {
      case GAMES.TABOO:
        return {
          ...TABOO_CONFIGS,
          AVAILABLE_TEAMS: isWholeClass
            ? [TABOO_CONFIGS.AVAILABLE_TEAMS[0]]
            : TABOO_CONFIGS.AVAILABLE_TEAMS
        };

      case GAMES.HOTSEAT:
        return HOTSEAT_CONFIGS;

      case GAMES.JUMBLE:
        return JUMBLE_CONFIGS;

      default:
        return TABOO_CONFIGS;
    }
  }, [gameType, isWholeClass]);

  const initValues = useMemo(() => {
    return {
      numberOfTeams:
        gameSetup?.numberOfTeams ?? gameConfigs.AVAILABLE_TEAMS[0].id,
      numberOfStudents:
        gameSetup?.numberOfStudents ?? gameConfigs.NUMBER_OF_STUDENTS[0].id,
      observations: roomInfo?.observations ?? [],
      teams: roomInfo?.teams ?? [],
      users: roomInfo?.users ?? [],
      canStudentChangeTeam: gameSetup?.CanStudentChangeTeam ?? false
    };
  }, [
    gameConfigs.AVAILABLE_TEAMS,
    gameConfigs.NUMBER_OF_STUDENTS,
    gameSetup?.CanStudentChangeTeam,
    gameSetup?.numberOfStudents,
    gameSetup?.numberOfTeams,
    roomInfo?.observations,
    roomInfo?.teams,
    roomInfo?.users
  ]);

  const methods = useResolverForm<IFormData>({
    schema: AppValidation.init(),
    configs: {
      values: initValues
    }
  });

  const [numberOfTeams, teams, users, observations] = useWatch({
    control: methods.control,
    name: ['numberOfTeams', 'teams', 'users', 'observations']
  });

  useMount(() => {
    const sound = '/sounds/waiting_room.mp3';

    if (IS_TEACHER) {
      AppAudio.playSound(sound, { loop: true });
    }

    return () => {
      if (IS_TEACHER) {
        AppAudio.stopSound();
      }
    };
  });

  const onStartGame = useCallback(() => {
    AppAudio.playSound('/sounds/launch_game.mp3', {
      cb: () => {
        const map = users.reduce<Record<string, number>>((acc, cur) => {
          if (acc[cur.username.toLowerCase()]) {
            acc[cur.username.toLowerCase()] += 1;
          } else {
            acc[cur.username.toLowerCase()] = 1;
          }
          return acc;
        }, {});

        const arr = Object.keys(map).filter(i => map[i] > 1);

        if (arr.length > 0) {
          UIUtils.snackBar.open({
            message: `Some students have the same name [${arr.join(
              ', '
            )}]. Please set different names for all students`,
            status: 'danger'
          });
        } else {
          AppSignalR.emit(SIGNALR_EVENTS.START_GAME, {});
        }
      }
    });
  }, [users]);

  const { onRemovePopup } = usePopup();

  const onTerminateRoom = useCallback(() => {
    onRemovePopup({
      title: trans('common.terminate_room'),
      content: trans('common.terminate_room_message'),
      buttons: [
        {
          variant: 'outline',
          title: trans('common.cancel')
        },
        {
          key: 'main',
          variant: 'solid',
          title: trans('common.terminate'),
          onPress: () => AppSignalR.emit(SIGNALR_EVENTS.TERMINATE_ROOM, {}),
          withDismiss: true
        }
      ]
    });
  }, [onRemovePopup]);

  const teamObj = useMemo(
    () =>
      users.reduce<Record<string, number>>((acc, cur) => {
        if (cur.teamId) {
          if (acc[cur.teamId]) {
            acc[cur.teamId] += 1;
          } else {
            acc[cur.teamId] = 1;
          }
        }
        return acc;
      }, {}),
    [users]
  );

  const isDisabled = useMemo(() => {
    if (isEmpty(teamObj)) {
      return true;
    }

    const numberOfStudents = gameSetup?.numberOfStudents || 0;

    if (
      Object.values(teamObj).some(
        i => i > numberOfStudents || (i < 2 && !isJumble)
      )
    ) {
      return true;
    }

    return false;
  }, [gameSetup?.numberOfStudents, isJumble, teamObj]);

  const isMobile = KDims.isMobile();

  const numberOfStudentsOptions = useMemo(() => {
    const wordCount = (gameSetup?.words ?? []).length;

    switch (gameType) {
      case GAMES.TABOO:
        return gameConfigs.NUMBER_OF_STUDENTS.filter(
          i => i.id <= wordCount / 2
        );

      case GAMES.HOTSEAT:
      case GAMES.JUMBLE:
        return gameConfigs.NUMBER_OF_STUDENTS.filter(i => i.id <= wordCount);

      default:
        return gameConfigs.NUMBER_OF_STUDENTS;
    }
  }, [gameConfigs.NUMBER_OF_STUDENTS, gameSetup?.words, gameType]);

  return (
    <FormProvider {...methods}>
      <KContainer.View marginV="1.75rem">
        <GameCode />

        <InGamePackageView
          marginT="1.75rem"
          marginB="2rem"
          br="2x"
          paddingH="1.5rem"
          paddingV="2rem"
        >
          <KLabel.Paragraph
            textAlign
            typo="TextXLgBold"
            color={KColors.highlight.normal}
            textTransform="uppercase"
          >
            {gameSetup?.type} {trans('common.game')}
          </KLabel.Paragraph>

          <KContainer.View marginT="1.5rem">
            <Controller
              name="numberOfTeams"
              control={methods.control}
              render={({ field }) => {
                return (
                  <KInput.Selection
                    {...field}
                    label={
                      trans('common.maximum_number_of_teams') +
                      `${isHotseat ? ' (Only 2 teams}' : ''}`
                    }
                    options={gameConfigs.AVAILABLE_TEAMS}
                    color={KColors.white}
                    disabled={!IS_TEACHER || isHotseat}
                    onChange={e => {
                      const _numberOfTeams = parseInt(e.target.value, 10);
                      const socketData: any = {
                        ...roomInfo,
                        gameSetup: {
                          ...gameSetup,
                          numberOfTeams: _numberOfTeams
                        }
                      };

                      if (_numberOfTeams < numberOfTeams) {
                        const remainingTeams = teams.slice(0, _numberOfTeams);
                        const remainingTeamIds = remainingTeams.map(t => t.id);

                        const _observations = [
                          ...(roomInfo?.observations ?? [])
                        ];
                        const _users: IStudentDto[] = [];

                        roomInfo?.users?.forEach(u => {
                          if (u.teamId && remainingTeamIds.includes(u.teamId)) {
                            _users.push(u);
                          } else {
                            _observations.push({ ...u, teamId: null });
                          }
                        });

                        set(socketData, 'teams', remainingTeams);
                        set(socketData, 'users', _users);
                        set(socketData, 'observations', _observations);

                        if (
                          _observations.length >
                          (roomInfo?.observations ?? []).length
                        ) {
                          onRemovePopup({
                            title: trans(
                              'common.change_maximum_number_of_teams'
                            ),
                            content: trans(
                              'common.change_maximum_number_of_teams_message'
                            ),
                            buttons: [
                              {
                                variant: 'outline',
                                title: trans('common.cancel')
                              },
                              {
                                key: 'main',
                                variant: 'solid',
                                title: trans('common.submit'),
                                onPress: () =>
                                  AppSignalR.emit(
                                    SIGNALR_EVENTS.UPDATE_ROOM_SETTINGS,
                                    socketData
                                  ),
                                withDismiss: true
                              }
                            ]
                          });
                        } else {
                          AppSignalR.emit(
                            SIGNALR_EVENTS.UPDATE_ROOM_SETTINGS,
                            socketData
                          );
                        }
                      } else if (_numberOfTeams > numberOfTeams) {
                        const _teams = [
                          ...teams,
                          ...Array.from(
                            Array(_numberOfTeams - numberOfTeams),
                            (_, idx) => {
                              return {
                                id: uuidv4(),
                                name: `Team ${teams.length + idx + 1}`,
                                tableId: null,
                                words: null,
                                finalScore: null
                              };
                            }
                          )
                        ];
                        set(socketData, 'teams', _teams);
                        AppSignalR.emit(
                          SIGNALR_EVENTS.UPDATE_ROOM_SETTINGS,
                          socketData
                        );
                      }
                    }}
                  />
                );
              }}
            />
          </KContainer.View>

          <KContainer.View marginT="1.5rem">
            <Controller
              name="numberOfStudents"
              control={methods.control}
              render={({ field }) => {
                return (
                  <KInput.Selection
                    {...field}
                    label={trans('common.maximum_member_of_teams')}
                    options={numberOfStudentsOptions}
                    color={KColors.white}
                    disabled={!IS_TEACHER}
                    onChange={e => {
                      const _numberOfStudents = parseInt(e.target.value, 10);
                      const socketData: any = {
                        ...roomInfo,
                        gameSetup: {
                          ...gameSetup,
                          numberOfStudents: _numberOfStudents
                        }
                      };

                      const max = Math.max(
                        ...(Object.values(teamObj) as number[])
                      );

                      if (max > _numberOfStudents) {
                        UIUtils.snackBar.open({
                          message: `Please update number of students in team less than or equal to ${max}`,
                          status: 'danger'
                        });
                      } else {
                        set(socketData, 'numberOfStudent', _numberOfStudents);
                        field.onChange(e);
                        AppSignalR.emit(
                          SIGNALR_EVENTS.UPDATE_ROOM_SETTINGS,
                          socketData
                        );
                      }
                    }}
                  />
                );
              }}
            />
          </KContainer.View>

          {IS_TEACHER && (
            <KContainer.View marginT="1.5rem">
              <Controller
                name="canStudentChangeTeam"
                control={methods.control}
                render={({ field }) => {
                  return (
                    <KListItem.Checkbox
                      {...field}
                      label={trans('common.students_can_change_team')}
                      checked={field.value}
                      color={KColors.white}
                      activeColor={KColors.white}
                      typo="TextXNmBold"
                      onChange={e => {
                        const socketData: any = {
                          ...roomInfo,
                          gameSetup: {
                            ...gameSetup,
                            CanStudentChangeTeam: e
                          }
                        };

                        field.onChange(e);
                        AppSignalR.emit(
                          SIGNALR_EVENTS.UPDATE_ROOM_SETTINGS,
                          socketData
                        );
                      }}
                    />
                  );
                }}
              />
            </KContainer.View>
          )}

          <KContainer.View marginT="1.5rem">
            <KLabel.Paragraph typo="TextNmBold" color={KColors.white}>
              {trans('common.observations')}
            </KLabel.Paragraph>

            {observations.map((i, idx) => {
              return (
                <ObservationItem
                  key={`observation-${i.teamId}-${idx}-${i.id}-${i.username}-${i.status}`}
                  item={i}
                  index={idx}
                />
              );
            })}
          </KContainer.View>

          {teams.map((i, idx) => {
            const teamUsers = users.filter(u => u.teamId === i.id);
            return (
              <TeamItem
                key={`team-${i.id}-${idx}-${JSON.stringify(teamUsers)}`}
                item={i}
              />
            );
          })}

          {/* {IS_TEACHER && teams.length < numberOfTeams && (
            <KButton.Dashed
              title={'+ ' + trans('common.add_more_team')}
              brC={KColors.white}
              textColor={KColors.white}
              width={'100%'}
              marginT="2rem"
              onPress={() => {
                append({
                  id: teams.length + 1,
                  name: '',
                  students: []
                });
              }}
            />
          )} */}
        </InGamePackageView>

        {IS_TEACHER && (
          <KContainer.View
            marginH={isMobile ? '1.25rem' : undefined}
            width={isMobile ? 'calc(100% - 3rem)' : '100%'}
          >
            <KButton.Solid
              title={trans('common.start_game')}
              onPress={onStartGame}
              width="100%"
              disabled={isDisabled}
            />

            <KButton.Solid
              title={trans('common.terminate_room')}
              onPress={onTerminateRoom}
              width="100%"
              marginT="1.25rem"
            />
          </KContainer.View>
        )}
      </KContainer.View>
    </FormProvider>
  );
};

WaitingRoom.displayName = 'Containers.InGame.WaitingRoom';

export default memo(WaitingRoom);
